'use client';

import { InventoryForm } from '@/components/Inventory/InventoryForm';
import { defaultContactNumber } from '@/constants/settings';
import { InventoryFormContext } from '@/contexts/InventoryFormContext';
import { LayoutContext } from '@/contexts/LayoutContext';
import { getUnlockPriceLabel } from '@/tools/inventory';
import { IDynamicsOptionSet } from '@/types/dynamicsForms';
import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { navigate } from '@lamesarv-sdk/tools';
import { getFacetCode } from '@lamesarv-sdk/tools/dist/inventory';
import { IInventory, InventoryField } from '@lamesarv-sdk/types';
import { Fragment, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

interface PdpFormProps {
  item: IInventory;
  locationsOptionSet: IDynamicsOptionSet;
  allowedSimilarMakeModelSearch?: boolean;
}

export const PdpForm = ({ item, locationsOptionSet, allowedSimilarMakeModelSearch }: PdpFormProps) => {
  const { openMobileInquire, submittedItems } = useContext(InventoryFormContext);
  const { context: layoutContext } = useContext(LayoutContext);

  const { defaultContactItem } = layoutContext;
  const { buttonLabel } = getUnlockPriceLabel(item);

  const wasSubmitted = submittedItems.has(item.id);
  const contactUrl = defaultContactItem?.url || `tel:${defaultContactNumber}`;

  const handleClickCTA = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();

    if (item[InventoryField.isSold]) {
      const similarRVsUrl = allowedSimilarMakeModelSearch
        ? `/search?make=${getFacetCode(item[InventoryField.make])}&model=${getFacetCode(item[InventoryField.model])}&ref=sold_similar`
        : `/search?body=${getFacetCode(item[InventoryField.body])}&ref=sold_similar`;

      navigate(similarRVsUrl);
      return;
    }

    openMobileInquire(item);
  };

  return (
    <Fragment>
      {!item[InventoryField.isSold] && (
        <div id="pdpform" className="pt-20 pb-[4.5rem] max-lg:hidden font-helvetica">
          <div className="flex flex-col container mx-auto gap-10 items-center">
            <h2 className="font-bold text-black-500 text-[2.25rem]">{buttonLabel}</h2>
            <div className="border border-black-200 rounded-md px-16 py-10 bg-white">
              <InventoryForm item={item} locationsOptionSet={locationsOptionSet} />
            </div>
          </div>
        </div>
      )}
      <div className="sticky-cta fixed py-4 px-5 bg-white shadow-[0px_-4px_10px_0px_#00000033] bottom-0 left-0 w-screen z-40 border-t border-black-350 flex gap-3 lg:hidden">
        {!wasSubmitted && (
          <button
            type="button"
            onClick={handleClickCTA}
            className={twMerge(
              item[InventoryField.isSold] && 'sold-shop-similar',
              'rounded bg-blue-500 text-white font-bold py-3 flex items-center justify-center flex-1 hover:bg-blue-600',
            )}
            data-tag="cta-button-pdp-fixed-footer"
            data-testid="rvunit-cta-mobile"
          >
            {buttonLabel}
          </button>
        )}
        <a
          href={contactUrl}
          className={twMerge(
            'py-3 px-5 rounded border border-blue-500 hover:bg-blue-50 bg-white text-blue-500 flex items-center justify-center gap-2',
            wasSubmitted && 'flex-1',
          )}
          data-testid="rvunit-call"
        >
          <FontAwesomeIcon icon={faPhone} className="size-4" />
          <span className="font-bold">Let's talk</span>
        </a>
      </div>
    </Fragment>
  );
};
